import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEGMENT_MAP, isDynamicId } from '@/shared/constants/pageTitleSegments';

export interface BreadcrumbItem {
  label: string;
  url?: string | undefined;
  /** Optional tooltip text shown on hover over this breadcrumb item. */
  tooltip?: string;
}

/**
 * Build the parent URL for a given depth in the path segments array.
 * e.g. segments = ['admin','runs','42'] depth=1 → '/admin/runs'
 */
const buildUrl = (segments: string[], depth: number): string =>
  '/' + segments.slice(0, depth + 1).join('/');

/**
 * useBreadcrumbs
 *
 * Automatically derives breadcrumbs from the current URL pathname.
 * Each path segment is mapped to a human-readable label via SEGMENT_MAP.
 * Dynamic ID segments are skipped (they don't add a breadcrumb item on their own).
 *
 * The last breadcrumb item has no `url` (it is the current page).
 */
export const useBreadcrumbs = (): BreadcrumbItem[] => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);

  const crumbs: BreadcrumbItem[] = [];

  for (let i = 0; i < segments.length; i++) {
    const raw = segments[i];
    const lower = raw.toLowerCase();

    // Skip pure dynamic ID segments — they don't produce their own breadcrumb
    if (isDynamicId(raw)) {
      continue;
    }

    const mapping = SEGMENT_MAP[lower];
    const label = mapping ? t(mapping.i18nKey, mapping.fallback) : raw;
    const url = buildUrl(segments, i);
    const isLast = i === segments.length - 1;

    const crumb: BreadcrumbItem = { label };
    if (!isLast) {
      crumb.url = url;
    }
    crumbs.push(crumb);
  }

  return crumbs;
};

export default useBreadcrumbs;